import React, { useContext, useMemo } from 'react';

import { Button, Flex, Space, Table, type TableColumnsType } from 'antd';
import { useNavigate } from 'react-router-dom';
import DashboardTemplate from 'src/templates/DashboardTemplate';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Module } from 'src/interfaces/entities/module';
import EditModule from 'src/pages/observers/module/ListModulesPage/components/EditModule';
import DeleteModule from 'src/pages/observers/module/ListModulesPage/components/DeleteModule';
import { useGetModulesQuery } from 'src/redux/services/centralApi';
import IconAsText from 'src/components/IconAsText';
import antdIconsMap from 'src/common/mapping/antdIconsMap';
import { AuthContext } from 'src/AuthProvider';
import { checkRole } from 'src/common/helpers/checkRole';

export default function ListModulesPage() {
	const navigate = useNavigate();
	const { user, } = useContext(AuthContext);

	const {
		data: modulesData,
		refetch,
		isLoading: isLoadingModulesData,
		isFetching: isFetchingModules,
	} = useGetModulesQuery({});

	const columns = useMemo<TableColumnsType<Module>>(
		() => [
			{
				dataIndex: 'icon',
				title: 'Ícone',
				width: 60,
				render: (value: keyof typeof antdIconsMap) => (
					<Flex align='center' justify='center'>
						<IconAsText icon={value} fontSize={20} />
					</Flex>
				),
			},
			{
				dataIndex: 'name',
				title: 'Nome',
				key: 'name',
			},
			{
				dataIndex: 'short_description',
				title: 'Breve descrição',
				render: (value: string) => {
					if (value && value.length > 50) {
						return <div title={value}>{value.substring(0, 47) + '...'}</div>;
					}
					return value;
				},
			},
			{
				dataIndex: 'target',
				title: 'ERP',
				key: 'target',
			},
			{
				dataIndex: 'team',
				title: 'Time',
				key: 'team',
			},
			{
				dataIndex: 'is_active',
				title: 'Ativo',
				key: 'is_active',
				filters: [
					{ text: 'Ativo', value: true, },
					{ text: 'Inativo', value: false, },
				],
				onFilter: (value, record) => record.is_active === value,
				render: (value: boolean) => value ? 'Ativo' : 'Inativo',
			},
			{
				dataIndex: 'creation_date',
				title: 'Data de Criação',
				key: 'creation_date',
			},
			{
				title: 'Ações',
				key: 'actions',
				render: (_, record) =>
					(checkRole('super_admin', { user, }) || record.partner_id) &&
<Space>
	<DeleteModule row={record} />
	<EditModule row={record} />
</Space>,
			},
		],
		[ user, ]
	);

	return (
		<DashboardTemplate pageTitle="Listar módulos">
			<Flex style={{ padding: 5, marginBottom: 8, }} gap={8} justify="end">
				<Button
					type="primary"
					onClick={() => navigate('/observador/modulo/adicionar')}
					icon={<PlusOutlined />}
				>
Adicionar
				</Button>
				<Button
					type="text"
					onClick={refetch}
					icon={<ReloadOutlined />}
				/>
			</Flex>
			<Table<Module>
				columns={columns}
				dataSource={modulesData ?? []}
				rowKey="module_id"
				loading={isLoadingModulesData || isFetchingModules}
				size="small"
			/>
		</DashboardTemplate>
	);
}
